import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react";
import { ImportTemplateListResponse } from "../../../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../../../services/TranslationService";
import DictionaryService from "../../../../../services/DictionaryService";
import { DictionaryListResponse } from "../../../../../entities/dictionary/DictionaryListResponse";
import FloatingPanelService from "../../../../shared/FloatingPanel";
import { TextEditor } from "../../../../shared/components/Editors";
import { FloatingPanelFooter } from "../../../../shared/components/FloatingPanelFooter";
import { useHistory } from "react-router-dom";
import ModalService from "../../../../shared/bootstrap/modal/Modal";
import { ToastService } from "../../../../shared/bootstrap/Toast";

const ImportDictionaries = ({
  data,
  model,
  setModel
}: {
  data?: ImportTemplateListResponse.Item;
  model: Partial<ImportTemplateListResponse.Item>;
  setModel: Dispatch<SetStateAction<Partial<ImportTemplateListResponse.Item>>>;
}) => {
  const { translate } = TranslationService;
  const [dictionaries, setDictionaries] = useState<DictionaryListResponse>();

  const getDictionaries = useCallback(async () => {
    const result = await DictionaryService.getList();
    if (result instanceof Error) {
      return;
    }
    setDictionaries(result);
  }, []);

  useEffect(() => {
    getDictionaries();
  }, [getDictionaries]);

  const newDictionary = async () => {
    await FloatingPanelService.showPanel({
      children: <ItemDictionaryEdit getDictionaries={getDictionaries} />,
      title: translate.Dictionary,
      width: 650,
      height: 250,
      position: "center"
    });
  };

  const showDictionaryToEdit = async (
    dicc: DictionaryListResponse.Dictionary
  ) => {
    await FloatingPanelService.showPanel({
      children: (
        <ItemDictionaryEdit
          dicctionary={dicc}
          getDictionaries={getDictionaries}
        />
      ),
      title: translate.Dictionary,
      width: 650,
      height: 250,
      position: "center"
    });
  };

  const exportDictionary = async (dicc: DictionaryListResponse.Dictionary) => {
    try {
      await DictionaryService.export(dicc.ImportDictionaryID);
    } catch (error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
    }
  };

  const deleteDictionary = (dicc: DictionaryListResponse.Dictionary) => {
    ModalService.showDefaultModal({
      acceptButtonLabel: translate.Delete,
      message: TranslationService.translate.AreYouSureDeleteDictionary.replace(
        "{1}",
        dicc.Name
      ),
      onAcceptClick: async () => {
        try {
          await DictionaryService.delete(dicc.ImportDictionaryID);
          await getDictionaries();

          ToastService.showToast(
            TranslationService.translate.DictionaryDeletedSuccessfully,
            undefined,
            "success"
          );
        } catch (error) {
          ToastService.showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        }
      },
      acceptButtonClassName: "btn btn-danger",
      title: TranslationService.translate.Delete,
      closeButtonLabel: TranslationService.translate.Cancel
    });
  };

  return (
    <div className="card px-5">
      <div className="d-flex flex-row flex-nowrap justify-content-between align-items-center my-4">
        <h2>{TranslationService.translate.Dictionaries}</h2>
        <div className="d-flex align-items-center">
          <button className="btn btn-primary" onClick={newDictionary}>
            <i className="fal fa-plus" /> {translate.NewDictionary}
          </button>
        </div>
      </div>
      <div className="d-flex row">
        <div className="col-8">
          <table className={"table genericTable table-ellipsis mb-0"}>
            <thead>
              <tr>
                <th>{translate.Value}</th>
                <th></th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {dictionaries?.list.map((x) => (
                <tr
                  key={x.ImportDictionaryID}
                  className="pointer show-child-on-hover"
                >
                  <td
                    className="col-11"
                    onClick={(event) => {
                      showDictionaryToEdit(x);
                      event.stopPropagation();
                    }}
                  >
                    {x.Name}
                  </td>
                  <td className="col-auto" onClick={() => exportDictionary(x)}>
                    <i className="fa-regular fa-arrow-down-to-line pointer"></i>
                  </td>
                  <td className="col-auto" onClick={() => deleteDictionary(x)}>
                    <i className="fa-regular fa-trash-can px-2"></i>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="col-6"></div>
    </div>
  );
};

const ItemDictionaryEdit = ({
  dicctionary,
  getDictionaries
}: {
  dicctionary?: DictionaryListResponse.Dictionary;
  getDictionaries: () => void;
}) => {
  const [dictionaryName, setDictionaryName] = useState(dicctionary?.Name);
  const history = useHistory();

  const saveDictionary = async () => {
    if (!dictionaryName) return;
    try {
      const result = await DictionaryService.set({
        id: dicctionary?.ImportDictionaryID,
        dictionaryname: dictionaryName
      });
      if (result instanceof Error) {
        throw new Error();
      } else {
        ToastService.showToast(
          TranslationService.translate.SavedChanges,
          undefined,
          "success"
        );
      }
    } catch (error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
    } finally {
      await getDictionaries();
      FloatingPanelService.hidePanel();
    }
  };

  const goToConfiguration = () => {
    history.push(
      `/${TranslationService.currentLanguage}/import/dictionarytemplate`,
      {
        ...dicctionary,
        Name: dictionaryName
      }
    );
    FloatingPanelService.hidePanel();
  };

  return (
    <>
      <div className="tabBody">
        <div className="row align-items-center mb-2 g-0">
          <div className="col-2 pe-2">
            <label className="form-label mb-0">
              {TranslationService.translate.Name}
            </label>
          </div>
          <div className="col pe-2">
            <TextEditor
              onChange={setDictionaryName}
              defaultValue={dictionaryName}
            />
          </div>
        </div>
      </div>

      <FloatingPanelFooter>
        {dicctionary && (
          <button className="btn me-3" onClick={goToConfiguration}>
            {TranslationService.translate.Configure}
          </button>
        )}
        <button className="btn btn-primary me-3" onClick={saveDictionary}>
          {TranslationService.translate.Save}
        </button>
        <button
          className="btn me-3"
          onClick={() => FloatingPanelService.hidePanel()}
        >
          {TranslationService.translate.Close}
        </button>
      </FloatingPanelFooter>
    </>
  );
};
export default ImportDictionaries;
